import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { useEffect, useState } from "react";
import { BUSINESS, IMAGES } from "../data/business";
import { Button } from "./ui/button";

const NAV = [
  { to: "/", label: "Start" },
  { to: "/stylizacja-paznokci-katowice", label: "Paznokcie" },
  { to: "/podologia-katowice", label: "Podologia" },
  { to: "/kosmetyka-twarzy-katowice", label: "Kosmetyka twarzy" },
  { to: "/cennik", label: "Cennik" },
  { to: "/o-nas", label: "O nas" },
  { to: "/blog", label: "Blog" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl border-b border-[var(--beautix-line)]/60"
            : "backdrop-blur-md border-b border-transparent"
        }`}
        style={{
          backgroundColor: scrolled
            ? "rgba(222, 198, 165, 0.25)"
            : "rgba(222, 198, 165, 0.10)",
        }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-24 md:h-28">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
            onClick={() => setOpen(false)}
          >
            <img
              src={IMAGES.logo}
              alt="Logo BEAUTIX — Studio Kosmetyki Nowoczesnej Katowice"
              className="h-12 sm:h-14 md:h-16 lg:h-[72px] w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Menu główne">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide font-medium link-underline transition-colors ${
                    isActive
                      ? "text-[var(--beautix-pink)]"
                      : "text-[var(--beautix-ink)] hover:text-[var(--beautix-pink)]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${BUSINESS.phones[1].tel}`}
              data-testid="header-call-btn"
              className="inline-flex items-center gap-2 bg-[var(--beautix-pink)] hover:bg-[var(--beautix-pink-dark)] text-white px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              {BUSINESS.phones[1].number}
            </a>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden relative z-[60] w-11 h-11 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-md text-[var(--beautix-ink)] border border-[var(--beautix-line)]/60"
            aria-label={open ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.75} /> : <Menu className="w-6 h-6" strokeWidth={1.75} />}
          </button>
        </div>
      </header>

      {/* Mobile menu — rendered as sibling to header to avoid backdrop-filter stacking issues */}
      <div
        className={`lg:hidden fixed inset-x-0 top-24 md:top-28 bottom-0 z-40 bg-white transform transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        data-testid="mobile-menu"
        aria-hidden={!open}
      >
        <div className="flex flex-col p-8 gap-6 h-full overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `font-serif-display text-3xl ${
                  isActive
                    ? "text-[var(--beautix-pink)]"
                    : "text-[var(--beautix-ink)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="mt-auto flex flex-col gap-3">
            <a
              href={`tel:${BUSINESS.phones[1].tel}`}
              className="inline-flex items-center justify-center gap-2 bg-[var(--beautix-pink)] text-white px-6 py-4 rounded-full text-sm font-medium"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              Zadzwoń {BUSINESS.phones[1].number}
            </a>
            <a
              href={`tel:${BUSINESS.phones[0].tel}`}
              className="inline-flex items-center justify-center gap-2 border border-[var(--beautix-line)] text-[var(--beautix-ink)] px-6 py-4 rounded-full text-sm font-medium"
            >
              Salon {BUSINESS.phones[0].number}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
